import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchStocks, StockItem } from '../slices/stocksSlice';
import { RootState } from '../store';
import ItemCard from './ItemCard';
import ItemDrawer from './ItemDrawer';
import Login from '../pages/Login';
import '../pages/Login.css';
import './Catalogue.css';
import { useLang, translations, gmccelist as GMC_CE_LIST } from '../i18n';
import LanguageDropdown from './LanguageDropdown';
import SimpleModal from './SimpleModal';
import Footer from './Footer';
import { useAuth } from '../contexts/AuthContext';

const FAKE_ITEMS = Array.from({ length: 20 }).map((_, i) => ({
  article_sheet: i + 1,
  article_sheet_obj: {
    article_sheet_id: `FAKE-${i + 1}`,
    gmc1: 'Bureau',
    gmc1_en: 'Desk',
    gmc2: 'GMC01552',
    gmc2_en: 'GMC01552',
    gmc3: 'GMC01552',
    gmc3_en: 'GMC01552',
    gmc4: 'GMC01552',
    gmc4_en: 'GMC01552',
    id: i + 1,
    itg_number: '',
    its_number: '',
    label: `Article fictif ${i + 1}`,
    label_en: `Fictional article ${i + 1}`,
    manutan_link: 'https://www.google.fr',
    photos: [process.env.PUBLIC_URL + '/photo-absente.png'],
    pid_number: ''
  },
  available_stock: Math.floor(Math.random() * 10) + 1,
  confirmed_reserved_stock: 0,
  id: i + 1,
  pending_stock: 0,
  temporary_reserved_stock: 0,
  total_stock: 10
}));

const PAGE_SIZE = 10;

const Catalogue: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { lang } = useLang();
  const t = translations[lang];
  const { user, logout } = useAuth();
  // GMC_CE_LIST est importé depuis i18n.tsx (tableau [{fr, en}])
  const { items, status } = useSelector((state: RootState) => state.stocks);
  const [selectedItem, setSelectedItem] = useState<StockItem | null>(null);
  const [page, setPage] = useState(1);
  const [displayed, setDisplayed] = useState<StockItem[]>([]);
  const [search, setSearch] = useState('');
  const [typeFiche, setTypeFiche] = useState<'all' | 'usage' | 'stock'>('all');
  const [minStock, setMinStock] = useState('');
  const [gmcce, setGmcce] = useState('');
  const [prixOp, setPrixOp] = useState<'gt' | 'lt'>('gt');
  const [prix, setPrix] = useState('');
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'available' | 'pending' | 'others'>('available');

  // Filtrage selon la recherche
  const filterItems = React.useCallback((list: StockItem[]) => {
    let filtered = list;
    
    // Filtre par onglet (appliqué en premier)
    if (activeTab === 'available') {
      filtered = filtered.filter(item => Number(item.available_stock) > 0);
    } else if (activeTab === 'pending') {
      filtered = filtered.filter(item => Number(item.pending_stock) > 0 && Number(item.available_stock) === 0);
    } else if (activeTab === 'others') {
      filtered = filtered.filter(item => Number(item.available_stock) === 0 && Number(item.pending_stock) === 0);
    }

    // Recherche textuelle
    if (!search.trim()) {
      filtered = filtered;
    } else {
      const s = search.trim().toLowerCase();
      filtered = filtered.filter(item =>
        item.article_sheet_obj.article_sheet_id.toLowerCase().includes(s) ||
        item.article_sheet_obj.label.toLowerCase().includes(s) ||
        item.article_sheet_obj?.pid_number?.toLowerCase()?.includes(s) 
      );
    }
    // Filtre type fiche
    if (typeFiche === 'usage') filtered = filtered.filter(item => item.article_sheet_obj.tag === 'Usage Interne');
    if (typeFiche === 'stock') filtered = filtered.filter(item => item.article_sheet_obj.tag !== 'Usage Interne');
    // Filtre min stock
    if (minStock && !isNaN(Number(minStock))) filtered = filtered.filter(item => Number(item.available_stock) >= Number(minStock));
    // Filtre GMC CE (robuste, ignore la casse et les espaces)
    if (gmcce) {
      filtered = filtered.filter(item => {
        const val = (item.article_sheet_obj.gmcce || '').toString().trim().toLowerCase();
        return val === gmcce.trim().toLowerCase();
      });
    }
    // Filtre prix final
    if (prix && !isNaN(Number(prix))) {
      filtered = filtered.filter(item => {
        const val = Number(item.article_sheet_obj.pdv_final);
        if (isNaN(val)) return false;
        return prixOp === 'gt' ? val >= Number(prix) : val <= Number(prix);
      });
    }
    return filtered;
  }, [search, typeFiche, minStock, gmcce, prixOp, prix, activeTab]);

  useEffect(() => {
    dispatch(fetchStocks() as any);
  }, [dispatch]);

  useEffect(() => {
    if (status === 'succeeded' && items.length > 0) {
      const reversed = [...items].reverse();
      setDisplayed(filterItems(reversed).slice(0, PAGE_SIZE));
      setPage(1);
    } else if (status !== 'succeeded') {
      setDisplayed(filterItems([...FAKE_ITEMS].reverse()).slice(0, PAGE_SIZE));
      setPage(1);
    }
  }, [status, items, search, filterItems]);

  useEffect(() => {
    const filtered = status === 'succeeded' ? filterItems([...items].reverse()) : filterItems([...FAKE_ITEMS].reverse());
    setDisplayed(filtered.slice(0, PAGE_SIZE * page));
  }, [page, status, items, search, filterItems]);

  // Infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      const filtered = status === 'succeeded' ? filterItems([...items].reverse()) : filterItems([...FAKE_ITEMS].reverse());
      if (
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 100 &&
        displayed.length < filtered.length
      ) {
        setPage((prev) => prev + 1);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [displayed, items, status, search, filterItems]);

  if (!user) {
    return <Login onLogin={() => {}} />;
  }

  return (
    <>
  <div className="catalogue-top">
        <div style={{display:'flex',alignItems:'center',gap:16,padding:'16px 32px',width:'100%',boxSizing:'border-box'}}>
  <img src={process.env.PUBLIC_URL + '/recat-logo-64.png'} alt="Logo" style={{height:40}} />
  <img src={process.env.PUBLIC_URL + '/manutan-familly-logo.jpg'} alt="Manutan" style={{height:40}} />
        <input
          type="text"
          placeholder={t.searchPlaceholder}
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          style={{flex:1, border:'1.5px solid #c7d6e7', borderRadius:8, padding:'10px 14px', fontSize:'1.05rem'}}
        />
        <button
          id='my-account-button'
          title={t.accountLabel}
          onClick={() => navigate('/mon-compte')}
          style={{display:'inline-flex',alignItems:'center',marginLeft:12,color:'inherit',cursor:'pointer',background:'none',border:'none'}}
        >
          <img src={process.env.PUBLIC_URL + '/account.png'} alt={t.accountLabel} style={{height:36}} />
          <span style={{marginLeft:8,fontSize:'1rem'}}>{t.accountLabel}</span>
        </button>
        <div style={{marginLeft:16,alignSelf:'center',display:'flex',gap:8,alignItems:'center'}}>
          <LanguageDropdown />
          <button
            onClick={() => {
              logout();
              navigate('/login');
            }}
            style={{
              background:'none',
              border:'none',
              cursor:'pointer',
              fontSize:'24px',
              padding:'4px 8px',
              display:'flex',
              alignItems:'center',
              justifyContent:'center',
              transition:'transform 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            title="Déconnexion"
          >
            ⏻
          </button>
        </div>
        </div>
        <div style={{display:'flex',gap:16,alignItems:'center',padding:'8px 32px 16px 32px',flexWrap:'wrap'}}>
        <div style={{border:'2px solid #2aa04aff',borderRadius:18,padding:'6px 14px',background:'#f8fbff',display:'flex',alignItems:'center',gap:8}}>
          <select value={typeFiche} onChange={e => { setTypeFiche(e.target.value as any); setPage(1); }} style={{borderRadius:8,padding:'8px 12px',fontSize:'1rem',border:'none',background:'transparent'}}>
            <option value="all">{t.typeAll}</option>
            <option value="usage">{t.typeUsage}</option>
            <option value="stock">{t.typeStock}</option>
          </select>
        </div>
        <div style={{border:'2px solid #2aa04aff',borderRadius:18,padding:'6px 14px',background:'#f8fbff',display:'flex',alignItems:'center',gap:8}}>
          <input
            type="number"
            min={0}
            placeholder={t.stockPlaceholder}
            value={minStock}
            onChange={e => { setMinStock(e.target.value); setPage(1); }}
            style={{borderRadius:8,padding:'8px 12px',fontSize:'1rem',border:'none',background:'transparent',width:100}}
          />
        </div>
        <div style={{border:'2px solid #2aa04aff',borderRadius:18,padding:'6px 14px',background:'#f8fbff',display:'flex',alignItems:'center',gap:8}}>
          <select value={gmcce} onChange={e => { setGmcce(e.target.value); setPage(1); }} style={{borderRadius:8,padding:'8px 12px',fontSize:'1rem',border:'none',background:'transparent'}}>
            <option value="">{t.gmcceAll}</option>
            {GMC_CE_LIST.map((g: any) => (
              <option key={g.fr} value={g.fr}>{lang === 'fr' ? g.fr : g.en}</option>
            ))}
          </select>
        </div>
        <div style={{border:'2px solid #2aa04aff',borderRadius:18,padding:'6px 14px',background:'#f8fbff',display:'flex',alignItems:'center',gap:8}}>
          <select value={prixOp} onChange={e => { setPrixOp(e.target.value as any); setPage(1); }} style={{borderRadius:8,padding:'8px 12px',fontSize:'1rem',border:'none',background:'transparent'}}>
            <option value="gt">{t.prixGt}</option>
            <option value="lt">{t.prixLt}</option>
          </select>
          <input
            type="number"
            min={0}
            placeholder={t.prixPlaceholder}
            value={prix}
            onChange={e => { setPrix(e.target.value); setPage(1); }}
            style={{borderRadius:8,padding:'8px 12px',fontSize:'1rem',border:'none',background:'transparent',width:100}}
          />
        </div>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center',padding:'12px 32px',borderBottom:'2px solid #e0e0e0'}}>
          <button
            onClick={() => { setActiveTab('available'); setPage(1); }}
            style={{
              padding:'10px 20px',
              fontSize:'1rem',
              fontWeight:600,
              border:'none',
              borderRadius:'8px 8px 0 0',
              cursor:'pointer',
              background: activeTab === 'available' ? '#2aa04aff' : '#f0f0f0',
              color: activeTab === 'available' ? '#fff' : '#666',
              transition:'all 0.2s ease'
            }}
          >
            {t.tabAvailable}
          </button>
          <button
            onClick={() => { setActiveTab('pending'); setPage(1); }}
            style={{
              padding:'10px 20px',
              fontSize:'1rem',
              fontWeight:600,
              border:'none',
              borderRadius:'8px 8px 0 0',
              cursor:'pointer',
              background: activeTab === 'pending' ? '#2aa04aff' : '#f0f0f0',
              color: activeTab === 'pending' ? '#fff' : '#666',
              transition:'all 0.2s ease'
            }}
          >
            {t.tabPending}
          </button>
          <button
            onClick={() => { setActiveTab('others'); setPage(1); }}
            style={{
              padding:'10px 20px',
              fontSize:'1rem',
              fontWeight:600,
              border:'none',
              borderRadius:'8px 8px 0 0',
              cursor:'pointer',
              background: activeTab === 'others' ? '#2aa04aff' : '#f0f0f0',
              color: activeTab === 'others' ? '#fff' : '#666',
              transition:'all 0.2s ease'
            }}
          >
            {t.tabOthers}
          </button>
        </div>
      </div>
      <div className="catalogue-content">
        <div className="catalogue-grid">
          {displayed.map((item: any) => (
            <ItemCard key={item.id} item={item} onDescriptif={() => setSelectedItem(item)} />
          ))}
          {selectedItem && (
            <ItemDrawer item={selectedItem} onClose={() => setSelectedItem(null)} />
          )}
        </div>
      </div>
      {accountModalOpen && (
        <SimpleModal title={t.accountLabel} onClose={() => setAccountModalOpen(false)}>
          <p>{t.accountTooltip}</p>
          <p>Cette fonctionnalité est en cours de développement. Elle sera disponible prochainement.</p>
        </SimpleModal>
      )}
    </>
  );
};

export default Catalogue;
